import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Skeleton, theme } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, SearchOutlined } from '@ant-design/icons';
import { EPButton } from '@/components';
import IconButton from '@/comp/common/IconButton';
import SortIcon from '@/comp/common/icons/SortIcon';
import FilterIcon from '@/comp/common/icons/FilterIcon';
import AssistantInsightCard from './AssistantInsightCard';
import { StationInsight } from '@/types/insights.types';
import * as S from './AIAssistantPage.styles';

const { useToken } = theme;

interface InsightPanelProps {
  isCollapsed: boolean;
  isMobile: boolean;
  activeTab: string;
  availableInsights: StationInsight[];
  archivedInsights: StationInsight[];
  isLoading: boolean;
  onInsightClick: (insightId: string) => void;
  onToggleCollapse: () => void;
  disabled?: boolean;
}

const InsightPanel: React.FC<InsightPanelProps> = observer(
  ({
    isCollapsed,
    isMobile,
    activeTab,
    availableInsights,
    archivedInsights,
    isLoading,
    onInsightClick,
    onToggleCollapse,
    disabled = false,
  }) => {
    const { t } = useTranslation();
    const { token } = useToken();

    const currentInsights = activeTab === 'available' ? availableInsights : archivedInsights;
    const isEmpty = !isLoading && currentInsights.length === 0;

    return (
      <>
        <S.InsightsPanel $isCollapsed={isCollapsed} $isMobile={isMobile}>
          <div className="container">
            <S.InsightsPanelHeader>
              <S.InsightsHeaderTop>
                <S.InsightsHeaderTitle>
                  <span>{t('AddInsightsToConversation')}</span>
                </S.InsightsHeaderTitle>
                <S.CloseIconButton onClick={onToggleCollapse}>
                  <MenuFoldOutlined style={{ fontSize: '24px', color: token.colorText }} />
                </S.CloseIconButton>
              </S.InsightsHeaderTop>

              <S.InsightsHeaderFilters>
                <IconButton
                  icon={<SortIcon width={18} height={18} />}
                  label="Sort"
                  showDropdown={true}
                  className="no-padding-left"
                  onClick={() => {
                    // TODO: Implement sort functionality
                  }}
                />
                <IconButton
                  icon={<FilterIcon width={18} height={18} />}
                  showDropdown={true}
                  className="no-padding-left"
                  onClick={() => {
                    // TODO: Implement filter functionality
                  }}
                />
                <IconButton
                  icon={<SearchOutlined style={{ fontSize: '14px' }} />}
                  label="Search"
                  className="no-padding-left"
                  onClick={() => {
                    // TODO: Implement search functionality
                  }}
                />
              </S.InsightsHeaderFilters>
            </S.InsightsPanelHeader>

            <S.InsightsList>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <S.InsightCardSkeleton key={`skeleton-${index}`}>
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </S.InsightCardSkeleton>
                ))
              ) : isEmpty ? (
                <S.EmptyState>
                  <p className="empty-message">
                    {activeTab === 'available'
                      ? t('NoAvailableInsights', 'No available insights')
                      : t('NoArchivedInsights', 'No archived insights')}
                  </p>
                </S.EmptyState>
              ) : (
                currentInsights.map(insight => (
                  <AssistantInsightCard
                    key={insight.id}
                    insight={insight}
                    onClick={() => !disabled && onInsightClick(insight.id)}
                    onAddClick={() => !disabled && onInsightClick(insight.id)}
                    showArrowIcon={false}
                    fixedHeight={false}
                    disabled={disabled}
                    actionButtons={[
                      {
                        label: t('AddToConversation', 'Add to conversation'),
                        onClick: () => !disabled && onInsightClick(insight.id),
                      },
                    ]}
                  />
                ))
              )}
            </S.InsightsList>
          </div>
        </S.InsightsPanel>
        {isCollapsed && (
          <div className="btn-collapse-container">
            <EPButton
              size="large"
              type="default"
              style={{
                background: token.colorBgElevated,
                borderWidth: 1,
                borderColor: token.colorBorderSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: token.marginXXS,
                padding: `0 ${token.paddingLG}px`,
                height: '40px',
                borderRadius: token.borderRadiusLG,
              }}
              onClick={onToggleCollapse}
            >
              <span>{t('StationInsights')}</span>
              <MenuUnfoldOutlined style={{ fontSize: '18px' }} />
            </EPButton>
          </div>
        )}
      </>
    );
  },
);

InsightPanel.displayName = 'InsightPanel';

export default InsightPanel;
